import React from 'react';
import { Database, AlertTriangle, FileText, Beaker, Activity, LucideIcon } from 'lucide-react';
import clsx from 'clsx';

interface AIDataContextSidebarProps {
    isDark: boolean;
    incidentsLength: number;
    activeIncidentsLength: number;
    criticalIncidentsLength: number;
    reportsLength: number;
    activeReportsLength: number;
    samplesLength: number;
    activeSamplesLength: number;
    criticalSamplesLength: number;
}

interface DataCardProps {
    isDark: boolean;
    title: string;
    icon: LucideIcon;
    iconColor: string;
    total: number;
    badgeColor: string;
    metrics: Array<{ label: string; value: string | number; valueColor?: string }>;
}

const DataCard: React.FC<DataCardProps> = ({
    isDark, title, icon: Icon, iconColor, total, badgeColor, metrics
}) => (
    <div className={clsx('p-3 rounded-lg border', isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-100')}>
        <div className="flex items-center justify-between mb-2">
            <span className={clsx('text-sm font-semibold flex items-center gap-2', iconColor)}>
                <Icon size={14} /> {title}
            </span>
            <span className={clsx('text-xs font-mono px-1.5 py-0.5 rounded-full', badgeColor)}>{total}</span>
        </div>
        <div className="space-y-1">
            {metrics.map((m, i) => (
                <div key={i} className="flex justify-between text-xs">
                    <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>{m.label}</span>
                    <span className={clsx(m.valueColor || (isDark ? 'text-white' : 'text-slate-900'))}>{m.value}</span>
                </div>
            ))}
        </div>
    </div>
);

export const AIDataContextSidebar: React.FC<AIDataContextSidebarProps> = ({
    isDark,
    incidentsLength,
    activeIncidentsLength,
    criticalIncidentsLength,
    reportsLength,
    activeReportsLength,
    samplesLength,
    activeSamplesLength,
    criticalSamplesLength
}) => {
    return (
        <div className={clsx(
            'w-64 flex flex-col rounded-xl border overflow-hidden transition-all duration-300',
            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'
        )}>
            <div className={clsx('p-4 border-b', isDark ? 'border-slate-700 bg-slate-900/50' : 'border-slate-100 bg-slate-50')}>
                <h2 className={clsx('font-bold flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                    <Database size={18} className="text-blue-500" />
                    Live Data
                </h2>
                <p className={clsx('text-xs mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Real-time system context
                </p>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-4">
                <DataCard
                    isDark={isDark} title="FRACAS" icon={AlertTriangle} iconColor="text-red-500" total={incidentsLength}
                    badgeColor="bg-red-100 text-red-700"
                    metrics={[
                        { label: 'Open', value: activeIncidentsLength },
                        { label: 'Critical', value: criticalIncidentsLength, valueColor: 'text-red-500 font-bold' }
                    ]}
                />

                <DataCard
                    isDark={isDark} title="8D Reports" icon={FileText} iconColor="text-blue-500" total={reportsLength}
                    badgeColor="bg-blue-100 text-blue-700"
                    metrics={[
                        { label: 'Active', value: activeReportsLength },
                        { label: 'Closed', value: reportsLength - activeReportsLength, valueColor: 'text-green-500' }
                    ]}
                />

                <DataCard
                    isDark={isDark} title="Lab Samples" icon={Beaker} iconColor="text-purple-500" total={samplesLength}
                    badgeColor="bg-purple-100 text-purple-700"
                    metrics={[
                        { label: 'Processing', value: activeSamplesLength },
                        { label: 'Critical', value: criticalSamplesLength, valueColor: 'text-purple-500 font-bold' }
                    ]}
                />

                <div className={clsx('p-3 rounded-lg border', isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-100')}>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold flex items-center gap-2 text-green-500">
                            <Activity size={14} /> Analytics
                        </span>
                        <span className="text-xs font-mono bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Active</span>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                        Weibull, MTBF, and Warranty models ready for querying.
                    </div>
                </div>
            </div>

            <div className={clsx('p-3 border-t text-xs text-center', isDark ? 'border-slate-700 text-slate-500' : 'border-slate-100 text-slate-400')}>
                <div className="flex items-center justify-center gap-1.5">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Data Link Active
                </div>
            </div>
        </div>
    );
};
